const richTextEditorTargetSelector =
  '[contenteditable]:not([contenteditable="false"]), .ProseMirror';

export function isRichTextEditorTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLElement && target.isContentEditable) {
    return true;
  }

  return (
    target instanceof Element && target.closest(richTextEditorTargetSelector) !== null
  );
}

/**
 * Determines if a DOM event originated from an editable text field.
 *
 * * **Why this exists:**
 * Due to DOM event bubbling, keystrokes typed into external components (like a side panel,
 * comment box, or Jira/Confluence rich text editor) will bubble up to the grid's global
 * event listeners. This guard prevents the grid from stealing keystrokes (like 'Enter'
 * or 'Backspace') that were intended for those external inputs.
 *
 * @param target - The `event.target` from a keyboard or mouse event.
 * @returns `true` if the target is a text input, textarea, or contenteditable element.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toUpperCase();

  // Check for standard HTML inputs or textareas
  if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
    const type = (target as HTMLInputElement).type;
    // We usually want to ignore checkboxes/radios, as they don't capture typing
    // and spacebar interactions on them shouldn't trigger grid cell edits.
    if (type !== 'checkbox' && type !== 'radio') {
      return true;
    }
  }

  // Check for Rich Text Editors (ProseMirror, Slate, standard contenteditable divs)
  if (isRichTextEditorTarget(target)) {
    return true;
  }

  return false;
}
